
import { Component, Prop, Vue } from "vue-property-decorator";
import RouteWithTrips, { RouteDestination } from "@/components/Passenger/models/RouteWithTrips";
import RouteDestinationDetails from "./RouteDestinationDetails.vue";
import RouteDetailsEta, { TripTimesWithStatus } from './RouteDetailsEta.vue';
import TimitMap, { LayerType } from "@/components/Map/TimitMap.vue";
import Trip from "@/components/Passenger/models/Trip";
import moment from "moment";

/** Minutes before we care about being late */
export const ETA_THRESHOLD_MINUTES = 3 as const;

/** Checks if the given ETA is within the detection threshold */
export function isEtaWithinThreshold(etaMinutes: number) {
  const positiveThreshold = ETA_THRESHOLD_MINUTES;
  const negativeThreshold = -ETA_THRESHOLD_MINUTES;
  return etaMinutes >= negativeThreshold && etaMinutes <= positiveThreshold;
}

interface IDriverEvent {
  title: string;
  time: string;
  timeDifference: number;
  icon: string;
}

@Component({
  components: {
    RouteDestinationDetails,
    RouteDetailsEta,
    TimitMap,
  },
})
export default class RouteDetails extends Vue {
  
  @Prop({ default: null }) routeWithTrips: RouteWithTrips | null;
  @Prop({ default: null}) tripId: number | null;
  @Prop({ default: false}) loadingRouteDetails: boolean;

  selectedTripId: number | null = null;
  mapLayerIsVisible = {
    [LayerType.Car]: true,
    [LayerType.Route]: true,
    [LayerType.Destination]: true,
  };

  get map() {
    return this.$refs.map as TimitMap;
  }

  get isDialogOpen() {
    return !!this.routeWithTrips;
  }

  get selectedTrip(): Trip {
    return this.routeWithTrips?.trips.find(trip => trip.id === (this.selectedTripId ?? this.tripId));
  }

  get selectedTripWithStatus() {
    return this.tripsWithStatuses.find(t => t.tripId === (this.selectedTripId ?? this.tripId));
  }

  get routeEta() {
    if (!this.routeWithTrips.destinations) {
      return 0;
    }

    for (let i = this.routeWithTrips.destinations.length - 1; i >= 0; i--) {
      const destination = this.routeWithTrips.destinations[i];

      const dropoffs = this.routeWithTrips.trips.filter(t => destination.dropOffs.includes(t.id));

        if (dropoffs.some(d => d.status?.eta) && destination.time) {
          const plannedMinutes = destination.time;
          const latestDropoffEstimate = dropoffs
            .filter(d => !!d.status?.eta)
            .map(d => moment(d.status.ata ?? d.status?.eta))
            .sort((a, b) => b.valueOf() - a.valueOf())[0];

          const actualMinutes = latestDropoffEstimate.hours() * 60 + latestDropoffEstimate.minutes();
          const diff = actualMinutes - plannedMinutes;
          return diff;
      }

      const pickups = this.routeWithTrips.trips.filter(t => destination.pickups.includes(t.id));
      if (pickups.some(d => d.status?.etd) && destination.time) {
        const latestPickupEstimate = pickups
          .filter(d => !!d.status?.etd)
          .map(d => moment(d.status.atd ?? d.status?.etd))
          .sort((a, b) => b.valueOf() - a.valueOf())[0];

        const plannedMinutes = destination.time;
        const actualMinutes = latestPickupEstimate.hours() * 60 + latestPickupEstimate.minutes();
        const diff = actualMinutes - plannedMinutes;
        return diff;
      }
    }

    return 0;
  }

  get tripsWithStatuses() {
    if (!this.routeWithTrips || !this.routeWithTrips.trips) {
      return [];
    }
    return this.routeWithTrips.trips.filter(t => t.status).map(t => ({
        tripId: t.id,
        startDateTime: t.startDateTime,
        status: t.status,
      }));
  }

  get routeCar() {
    return this.routeWithTrips?.carName || this.routeWithTrips?.carRegisterNumber;
  }

  get routeHasUnknownPlaces() {
    return !this.routeWithTrips.osrmRouteGeometry && this.routeWithTrips?.destinations.some(destination => !destination.address);
  }

  get driverEvents() {
    const events: IDriverEvent[] = [];
    if (this.selectedTrip.pickupTime) {
      events.push({
        title: this.$t("routeDetails.driverEvents.pickup"),
        time: this.selectedTrip.pickupTime,
        timeDifference: this.timeDiffInMins(this.selectedTrip.pickupTime, this.selectedTrip.startDateTime),
        icon: 'arrow_upward',
      });
    }
    if (this.selectedTrip.dropoffTime) {
      events.push({
        title: this.$t("routeDetails.driverEvents.dropOff"),
        time: this.selectedTrip.dropoffTime,
        timeDifference: this.timeDiffInMins(this.selectedTrip.dropoffTime, this.selectedTrip.endDateTime),
        icon: 'arrow_downward',
      });
    }
    if (this.selectedTrip.noShowTime) {
      events.push({
        title: this.$t("routeDetails.driverEvents.noShow"),
        time: this.selectedTrip.noShowTime,
        timeDifference: this.timeDiffInMins(this.selectedTrip.noShowTime, this.selectedTrip.startDateTime),
        icon: 'do_not_disturb',
      });
    }
    return events;
  }


  get mapLayerBtnConfig() {
    return {
      [LayerType.Car]: {
        showBtn: () => this.routeWithTrips.liveLocationAvailable || this.routeWithTrips.drivenRoute?.length > 1,
        layerIsVisible: this.mapLayerIsVisible[LayerType.Car],
        btnLabel: this.$t('routeDetails.drivenRoute'),
        layerType: LayerType.Car,
      },
      [LayerType.Route]: {
        showBtn: () => !!this.routeWithTrips.osrmRouteGeometry,
        layerIsVisible: this.mapLayerIsVisible[LayerType.Route],
        btnLabel: this.$t('routeDetails.route'),
        layerType: LayerType.Route, 
      },
      [LayerType.Destination]: {
        showBtn: () => this.routeWithTrips.destinations?.length > 0,
        layerIsVisible: this.mapLayerIsVisible[LayerType.Destination],
        btnLabel: this.$t('routeDetails.destinations'),
        layerType: LayerType.Destination,
      },
    };
  }

  get shownMapLayerBtns() {
    return Object.values(this.mapLayerBtnConfig).filter(config => config.showBtn());
  }

  get estimatedArrivals() {
    if (!this.routeWithTrips || !this.routeWithTrips.destinations || !this.routeWithTrips.trips) {
      return [];
    }

    type DateOrNull = Date|null; 
    const destinationEtas: DateOrNull[] = this.routeWithTrips.destinations.map(d => null);

    for (const destination of this.routeWithTrips.destinations) {
      const pickupTrips = this.routeWithTrips.trips
        .filter(t => destination.pickups.includes(t.id));
      const dropoffTrips = this.routeWithTrips.trips
        .filter(t => destination.dropOffs.includes(t.id));
      // Get destination driver event times
      const destinationEventTimes = pickupTrips.map(t => t.status?.arrivalToPickup ?? t.status?.atd) // Actual time of departure (start)
        .concat(dropoffTrips.map(t => t.status?.ata)) // Actual time at destination (end)
        .filter(t => !!t)
        .map(t => moment(t))
        // sort in time order
        .sort((a, b) => a.valueOf() - b.valueOf());
      
      if (destinationEventTimes.length > 0) {
        // If destination had any pickups, dropoffs or noshows. Display minimum time from them.
        destinationEtas[this.routeWithTrips.destinations.indexOf(destination)] = destinationEventTimes[0].toDate();
        continue;
      }

      // Get destination estimated times
      const destinationEstimateTimes = pickupTrips.map(t => t.status?.etd) // Estimated time of departure (start)
        .concat(dropoffTrips.map(t => t.status?.eta)) // Estimated time at destination (end)
        .filter(t => !!t)
        .map(t => moment(t))
        // sort in reverse order
        .sort((a, b) => b.valueOf() - a.valueOf());
      
      // Set max estimate time to destination
      if (destinationEstimateTimes.length > 0) {
        destinationEtas[this.routeWithTrips.destinations.indexOf(destination)] = destinationEstimateTimes[0].toDate();
      }
    }
    return destinationEtas;
  }

  isDefined(time: string | null | undefined): time is string {
    return typeof time === 'string';
  }

  isDefinedDateTime(time: Date | string | null | undefined): time is Date | string {
    return time instanceof Date || typeof time === 'string';
  }

  /** Finds the max deviation of estimated time from the planned start time of trips on the route */
  getMaxDeviationFromTrips() {
      if (!this.routeWithTrips || !this.routeWithTrips.trips) {
        return 0;
      }

      return this.routeWithTrips.trips.reduce((max, trip) => {
        const plannedStart = moment(trip.startDateTime);
        const eta = moment(trip.status?.etd ?? trip.startDateTime);
        const diff = eta.diff(plannedStart, 'minutes');

        // get the absolute maximum of the deviation, whether it's early or late
        const maxValue = Math.max(Math.abs(diff), Math.abs(max));

        if (maxValue === Math.abs(diff)) {
          return diff;
        }

        return max;
      }, 0);
  }

  getNewestTripEventForDestination(destination: RouteDestination, trips: Trip[], diff: number, previousDestinationEta: Date | null): Date {
    
    let allEventsFromDestination: Date[] = [];
    const pickups = trips.filter(t => destination.pickups.includes(t.id)).flatMap(t => t.pickupTime ? [new Date(t.pickupTime)] : []);
    const dropOffs = trips.filter(t => destination.dropOffs.includes(t.id)).flatMap(t => t.dropoffTime ? [new Date(t.dropoffTime)] : []);
    const noShows = trips.filter(t => destination.trips.includes(t.id)).flatMap(t => t.noShowTime ? [new Date(t.noShowTime)] : []);
    allEventsFromDestination = [...pickups, ...dropOffs, ...noShows].filter(d => !Number.isNaN(d.getTime()));
    
    if (allEventsFromDestination.length === 0) { 
      return moment().startOf('day').add(destination.time , 'minutes').toDate();
    }

    allEventsFromDestination.sort((a, b) => b.getTime() - a.getTime());
    const newestEvent = allEventsFromDestination[0];
    return newestEvent;
  }

  
  getCancelledByText(trip: Trip): string {
    if (!trip.cancelledBy) {
      return this.$t("passenger.arrival_departure.cancellation.cancelled_by_movit_user", "Cancelled from Movit.") 
    } 
    return trip.cancelledBy;
  }

  changeLayerVisibility(layerType: LayerType) {
    this.mapLayerIsVisible[layerType] = !this.mapLayerIsVisible[layerType];
    this.map.changeLayerVisibility(layerType);
  }

  timeDiffInMins(actual: Date | string, estimate: Date | string) {
    const difference = new Date(actual).getTime() - new Date(estimate).getTime();
    return Math.round(difference / 60000);
  }

  selectDestination(destination: RouteDestination) {
    if (!destination.trips.includes(this.selectedTrip.id)) {
      this.selectTrip(destination.trips[0]);
    }
  }

  selectTrip(tripId: number) {
    this.selectedTripId = tripId;
    this.map.selectDestinationByTripId(tripId);
  }

  closeDialog() {
    this.selectedTripId = null;
    this.map.clearMap();
    this.$emit("closeDialog");
  }

  showRouteWithDestinations() {
    this.map.showRouteWithDestinations(this.routeWithTrips, this.selectedTrip.id);
  }

  updateRouteDetails(trip: TripTimesWithStatus) {
    const foundTrip = this.routeWithTrips?.trips?.find(t => t.id === trip.tripId);
    if (!foundTrip) {
      return;
    }

    this.$emit('update', foundTrip);
  }
}
